// Plan Library v2 — premium-charge ledger view (Workstream D.2).
//
// Per-month charged history straight from the append-only premium_charges
// ledger: originals, reversals, and re-charges (scenarios 05/21), grouped by
// service month. Reversals reference the charge they reverse; nothing here is
// ever edited — corrections appear as new reversal + re-charge rows.

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronDown, Receipt } from "lucide-react";

interface PremiumCharge {
  id: number;
  clientId: number;
  employeeId: number | null;
  dependentId: number | null;
  enrollmentId: number;
  planId: number;
  serviceMonth: string;
  entryType: "charge" | "reversal";
  reversesChargeId: number | null;
  reason: string | null;
  amount: string;
  planYearStart: string | null;
  ratingAreaId: number | null;
  rateVersionKey: string | null;
  ageBand: string | null;
  tobaccoApplied: boolean | null;
  coverageTier: string | null;
  createdAt: string | null;
  planName?: string | null;
}

interface EmployeeLite {
  id: number;
  firstName: string | null;
  lastName: string | null;
}

interface PlanLite {
  id: number;
  name: string;
}

export function PremiumLedgerCard({
  clientId,
  employeeId,
  title = "Premium Ledger",
}: {
  clientId?: number;
  employeeId?: number;
  title?: string;
}) {
  const [openMonths, setOpenMonths] = useState<Record<string, boolean>>({});

  const params = new URLSearchParams();
  if (clientId) params.set("clientId", String(clientId));
  if (employeeId) params.set("employeeId", String(employeeId));

  const { data: charges = [], isLoading } = useQuery<PremiumCharge[]>({
    queryKey: ["/api/premium-charges", clientId ?? null, employeeId ?? null],
    queryFn: async () => {
      const res = await fetch(`/api/premium-charges?${params.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch premium ledger");
      return res.json();
    },
    enabled: !!clientId || !!employeeId,
  });

  const { data: employees = [] } = useQuery<EmployeeLite[]>({
    queryKey: ["/api/employees", { clientId: clientId ?? null }],
    queryFn: async () => {
      const res = await fetch(clientId ? `/api/employees?clientId=${clientId}` : "/api/employees", {
        credentials: "include",
      });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!clientId,
  });

  const { data: plans = [] } = useQuery<PlanLite[]>({
    queryKey: ["/api/plans"],
  });

  const employeeName = useMemo(() => {
    const map = new Map<number, string>();
    for (const e of employees) {
      map.set(e.id, `${e.firstName ?? ""} ${e.lastName ?? ""}`.trim() || `Employee #${e.id}`);
    }
    return map;
  }, [employees]);

  const planName = useMemo(() => {
    const map = new Map<number, string>();
    for (const p of plans ?? []) map.set(p.id, p.name);
    return map;
  }, [plans]);

  const months = useMemo(() => {
    const byMonth = new Map<string, PremiumCharge[]>();
    for (const c of charges) {
      const key = String(c.serviceMonth).slice(0, 7);
      const list = byMonth.get(key) || [];
      list.push(c);
      byMonth.set(key, list);
    }
    return Array.from(byMonth.entries())
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([month, rows]) => {
        const net = rows.reduce(
          (sum, r) => sum + (r.entryType === "reversal" ? -1 : 1) * Math.abs(Number(r.amount)),
          0,
        );
        return { month, rows, net, hasReversals: rows.some((r) => r.entryType === "reversal") };
      });
  }, [charges]);

  const fmtMonth = (m: string) =>
    new Date(`${m}-01T00:00:00`).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const money = (n: number) =>
    n.toLocaleString("en-US", { style: "currency", currency: "USD" });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Receipt className="h-4 w-4" />
          {title}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Charged history per month — originals, reversals and re-charges. Posted rows are
          immutable; corrections appear as reversal + re-charge entries.
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : months.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">
            No charges posted yet. Charges post monthly (or via Generate) once enrollments are
            active.
          </p>
        ) : (
          <div className="rounded-md border divide-y">
            {months.map(({ month, rows, net, hasReversals }) => {
              const open = openMonths[month] ?? false;
              return (
                <div key={month}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-muted/50"
                    onClick={() => setOpenMonths((prev) => ({ ...prev, [month]: !open }))}
                  >
                    <span className="flex items-center gap-2 font-medium">
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${open ? "" : "-rotate-90"}`}
                      />
                      {fmtMonth(month)}
                      {hasReversals && (
                        <Badge variant="outline" className="text-[10px]">
                          has reversals
                        </Badge>
                      )}
                    </span>
                    <span className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>{rows.length} entries</span>
                      <span className="font-mono font-medium text-foreground">{money(net)}</span>
                    </span>
                  </button>
                  {open && (
                    <div className="border-t bg-muted/30 overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs h-8">Member</TableHead>
                            <TableHead className="text-xs h-8">Plan</TableHead>
                            <TableHead className="text-xs h-8">Type</TableHead>
                            <TableHead className="text-xs h-8">Reason</TableHead>
                            <TableHead className="text-xs h-8">Version</TableHead>
                            <TableHead className="text-xs h-8">Band / Tier</TableHead>
                            <TableHead className="text-xs h-8 text-right">Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {rows.map((r) => (
                            <TableRow key={r.id}>
                              <TableCell className="py-1.5 text-xs">
                                {r.employeeId
                                  ? employeeName.get(r.employeeId) ?? `Employee #${r.employeeId}`
                                  : "—"}
                                {r.dependentId ? (
                                  <Badge variant="outline" className="ml-1 text-[10px]">
                                    dependent
                                  </Badge>
                                ) : null}
                              </TableCell>
                              <TableCell className="py-1.5 text-xs">
                                {r.planName ?? planName.get(r.planId) ?? `Plan #${r.planId}`}
                              </TableCell>
                              <TableCell className="py-1.5 text-xs">
                                {r.entryType === "reversal" ? (
                                  <Badge
                                    variant="outline"
                                    className="text-[10px] text-red-600 border-red-200"
                                  >
                                    reversal{r.reversesChargeId ? ` of #${r.reversesChargeId}` : ""}
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary" className="text-[10px]">
                                    charge
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell className="py-1.5 text-xs text-muted-foreground">
                                {r.reason ?? "—"}
                              </TableCell>
                              <TableCell className="py-1.5 text-xs font-mono text-muted-foreground">
                                {r.rateVersionKey || "—"}
                              </TableCell>
                              <TableCell className="py-1.5 text-xs text-muted-foreground">
                                {r.ageBand || r.coverageTier || "—"}
                                {r.tobaccoApplied ? " · tobacco" : ""}
                              </TableCell>
                              <TableCell
                                className={`py-1.5 text-xs text-right font-mono ${
                                  r.entryType === "reversal" ? "text-red-600" : ""
                                }`}
                              >
                                {r.entryType === "reversal" ? "−" : ""}
                                {money(Math.abs(Number(r.amount)))}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default PremiumLedgerCard;
